import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogBody,
  InputGroup,
  Tag,
  Tooltip,
  Button,
  ButtonGroup,
} from '@blueprintjs/core';
import { FieldDefinition } from '../../../../../../types/FieldDefinition';
import {
  buildTreeFromFieldDefinitions,
  FieldMappingTreeNode,
} from '../../../../../../utils/fieldMappingTreeBuilder';
import { findFieldByPath } from '../../../../../../lib/dataSync';
import { FieldMappingEntry } from '../../../../../../types/DataSyncViewModel';
import { FIELD_PATH_SEPARATOR } from '../../../../../../types/FieldDefinition';
import { getInvalidSourceFields } from '../../../../../../utils/fieldMappingValidator';
import { findBestMatch } from '../../../../../../lib/fieldMatcher';
import styles from './FieldSelectorDialog.module.sass';

interface FieldSelectorDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;

  /** Source field definitions to display in the tree */
  sourceFields: FieldDefinition[];

  /** Destination field definitions for validation */
  destinationFields: FieldDefinition[];

  /** The destination field path being mapped */
  destinationFieldPath: string;

  /** Existing field mappings for validation */
  existingMappings: FieldMappingEntry[];

  /** Currently selected source field path (optional) */
  selectedField?: string;

  /** Callback when a field is selected */
  onSelect: (fieldPath: string) => void;

  /** Callback to close the dialog */
  onClose: () => void;
}

/**
 * Helper to get the appropriate CSS class for a field type badge
 */
const getTypeBadgeClass = (type: string): string => {
  const typeMap: Record<string, string> = {
    string: styles.typeBadgeString,
    object: styles.typeBadgeObject,
    array: styles.typeBadgeArray,
    boolean: styles.typeBadgeBoolean,
    number: styles.typeBadgeNumber,
    integer: styles.typeBadgeNumber,
  };
  return typeMap[type.toLowerCase()] || styles.typeBadgeDefault;
};

/**
 * Helper to format the type label for display
 * For arrays of primitives, shows "array[type]"
 */
const getTypeLabel = (node: FieldMappingTreeNode): string => {
  if (node.type === 'array' && node.arrayItemType) {
    return `array [${node.arrayItemType}]`;
  }
  return node.type;
};

/**
 * Recursively filters tree nodes based on search query with parent-child matching
 * - If node matches: show node + all its children
 * - If any descendant matches: show node + filtered children
 */
const filterTreeNodes = (nodes: FieldMappingTreeNode[], query: string): FieldMappingTreeNode[] => {
  if (!query) {
    return nodes;
  }

  const lowerQuery = query.toLowerCase();

  return nodes.reduce<FieldMappingTreeNode[]>((acc, node) => {
    const nameMatches = node.name.toLowerCase().includes(lowerQuery) ||
                       node.displayName.toLowerCase().includes(lowerQuery);

    // Recursively filter children
    const filteredChildren = node.children.length > 0
      ? filterTreeNodes(node.children, query)
      : [];

    // Include node if:
    // 1. The node itself matches (show with all children)
    // 2. Any descendant matches (show with only matching descendants)
    if (nameMatches) {
      // Node matches: include all children
      acc.push({
        ...node,
        children: node.children,
      });
    } else if (filteredChildren.length > 0) {
      // Descendant matches: include parent with filtered children
      acc.push({
        ...node,
        children: filteredChildren,
      });
    }

    return acc;
  }, []);
};

/**
 * Renders a tree node with expand/collapse and selection
 */
const TreeNode: React.FC<{
  node: FieldMappingTreeNode;
  onSelect: (path: string) => void;
  expandedNodes: Set<string>;
  onToggleExpand: (path: string) => void;
  selectedField?: string;
  selectedNodeRef?: React.RefObject<HTMLDivElement>;
  isDisabled?: boolean;
  disabledFieldsMap: Map<string, string>;
}> = ({
  node,
  onSelect,
  expandedNodes,
  onToggleExpand,
  selectedField,
  selectedNodeRef,
  isDisabled = false,
  disabledFieldsMap,
}) => {
  const isExpanded = expandedNodes.has(node.path);
  const isSelected = selectedField === node.path;
  const hasChildren = node.children.length > 0;

  const handleClick = () => {
    // Don't allow selection if disabled
    if (isDisabled && node.isLeaf) {
      return;
    }

    if (node.isLeaf) {
      onSelect(node.path);
    } else {
      onToggleExpand(node.path);
    }
  };

  const disabledReason = disabledFieldsMap.get(node.path);

  const nodeContent = (
    <div
      ref={isSelected ? selectedNodeRef : undefined}
      className={`${styles.treeNode} ${
        hasChildren ? styles.treeNodeParent : ''
      } ${isSelected ? styles.treeNodeSelected : ''} ${
        isDisabled && node.isLeaf
          ? styles.treeNodeDisabled
          : styles.treeNodeClickable
      }`}
      onClick={handleClick}
      style={{ paddingLeft: `${16 + node.level * 24}px` }}
    >
      {/* Expand/collapse chevron for parent nodes */}
      {hasChildren ? (
        <button
          className={styles.expandButton}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(node.path);
          }}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? '▾' : '▸'}
        </button>
      ) : (
        <div className={styles.expandButtonSpacer} />
      )}

      {/* Field name */}
      <span
        className={`${styles.fieldName} ${
          !node.isLeaf ? styles.fieldNameParent : ''
        }`}
      >
        {node.displayName}
        {/* Inline type badge for parent nodes */}
        {
          <Tag
            round
            minimal
            className={`${getTypeBadgeClass(node.type)} ${
              styles.inlineTypeBadge
            }`}
          >
            {getTypeLabel(node)}
          </Tag>
        }
      </span>
    </div>
  );

  return (
    <div className={styles.treeNodeContainer}>
      {/* Wrap in tooltip if disabled - positioned at top */}
      {disabledReason ? (
        <Tooltip content={disabledReason} compact={true} minimal={true} hoverOpenDelay={350}>
          {nodeContent}
        </Tooltip>
      ) : (
        nodeContent
      )}

      {/* Render children if expanded */}
      {hasChildren && isExpanded && (
        <div className={styles.childrenContainer}>
          {node.children.map((child) => (
            <TreeNode
              key={child.path}
              node={child}
              onSelect={onSelect}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              selectedField={selectedField}
              selectedNodeRef={selectedNodeRef}
              isDisabled={disabledFieldsMap.has(child.path)}
              disabledFieldsMap={disabledFieldsMap}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const FieldSelectorDialog: React.FC<FieldSelectorDialogProps> = ({
  isOpen,
  sourceFields,
  destinationFields,
  destinationFieldPath,
  existingMappings,
  selectedField,
  onSelect,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [activeFilter, setActiveFilter] = useState<'all' | 'compatible' | 'suggested'>('all');
  const selectedNodeRef = useRef<HTMLDivElement>(null);
  const prevIsOpenRef = useRef(isOpen);

  // Reset filters when dialog opens (transitions from closed to open)
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      setSearchQuery('');
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  // Build tree from source fields
  const treeData = useMemo(() => {
    return buildTreeFromFieldDefinitions(sourceFields);
  }, [sourceFields]);

  // Build validation map for each source field path with reasons
  // Uses the reusable validation function that is decoupled from UI
  const disabledFieldsMap = useMemo(() => {
    return getInvalidSourceFields(
      destinationFieldPath,
      sourceFields,
      destinationFields,
      existingMappings
    );
  }, [destinationFieldPath, sourceFields, destinationFields, existingMappings]);

  // Get best match field paths using AI matcher
  const bestMatchFieldPaths = useMemo(() => {
    const destField = findFieldByPath(destinationFields, destinationFieldPath);
    if (!destField) {
      return new Set<string>();
    }

    // Exclude incompatible fields from suggestions
    const disabledPaths = new Set(disabledFieldsMap.keys());
    const bestMatches = findBestMatch(destField, sourceFields, disabledPaths);

    // Field names already use '/' separator from extractLeafFields
    return new Set(bestMatches.map((field) => field.name));
  }, [destinationFieldPath, destinationFields, sourceFields, disabledFieldsMap]);

  // Calculate compatible suggested fields (similarity-suggested AND not disabled)
  const compatibleSuggestedCount = useMemo(() => {
    let count = 0;
    bestMatchFieldPaths.forEach((path) => {
      if (!disabledFieldsMap.has(path)) {
        count++;
      }
    });
    return count;
  }, [bestMatchFieldPaths, disabledFieldsMap]);

  // Apply field filter (all vs available only) to tree
  const applyFieldFilter = useCallback((nodes: FieldMappingTreeNode[]): FieldMappingTreeNode[] => {
    if (activeFilter === 'all') {
      return nodes;
    }

    return nodes.reduce<FieldMappingTreeNode[]>((acc, node) => {
      // Apply filter to children first
      const filteredChildren = node.children.length > 0
        ? applyFieldFilter(node.children)
        : [];

      // Determine if this node should be included
      let includeNode = false;

      if (node.isLeaf) {
        // For leaf nodes, apply the appropriate filter
        if (activeFilter === 'compatible') {
          includeNode = !disabledFieldsMap.has(node.path);
        } else if (activeFilter === 'suggested') {
          includeNode = bestMatchFieldPaths.has(node.path) && !disabledFieldsMap.has(node.path);
        }
      } else {
        // For parent nodes, include if any child is included
        includeNode = filteredChildren.length > 0;
      }

      if (includeNode) {
        acc.push({
          ...node,
          children: filteredChildren,
        });
      }

      return acc;
    }, []);
  }, [activeFilter, disabledFieldsMap, bestMatchFieldPaths]);

  // Filter tree based on field filter and search query
  const filteredTreeData = useMemo(() => {
    let filtered = treeData;

    // Apply field filter first (all vs available)
    filtered = applyFieldFilter(filtered);

    // Then apply search query
    filtered = filterTreeNodes(filtered, searchQuery);

    return filtered;
  }, [treeData, searchQuery, applyFieldFilter]);

  // Expand all nodes by default when tree data changes
  useEffect(() => {
    const allPaths = new Set<string>();
    const collectPaths = (nodes: FieldMappingTreeNode[]) => {
      nodes.forEach((node) => {
        if (node.children.length > 0) {
          allPaths.add(node.path);
          collectPaths(node.children);
        }
      });
    };
    collectPaths(searchQuery || activeFilter !== 'all' ? filteredTreeData : treeData);
    setExpandedNodes(allPaths);
  }, [treeData, searchQuery, activeFilter, filteredTreeData]);

  // Scroll selected node into view when dialog opens and nodes are expanded
  useEffect(() => {
    if (isOpen && selectedField) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Double rAF ensures DOM has been painted
          if (selectedNodeRef.current) {
            selectedNodeRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }
        });
      });
    }
  }, [isOpen, selectedField, expandedNodes.size]);

  const handleToggleExpand = (path: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const handleSelect = (fieldPath: string) => {
    onSelect(fieldPath);
    onClose();
  };

  // Build destination field display info with all field types
  const destinationFieldInfo = useMemo(() => {
    const field = findFieldByPath(destinationFields, destinationFieldPath);
    if (!field) {
      return null;
    }

    // Build display name path with types for each segment
    const pathParts = destinationFieldPath.split(FIELD_PATH_SEPARATOR);
    const pathSegments: Array<{ displayName: string; type: string }> = [];
    let currentFields = destinationFields;

    for (const part of pathParts) {
      const currentField = currentFields.find((f) => f.name === part);
      if (currentField) {
        const displayName = currentField.display_name || currentField.name;
        const type = currentField.type || currentField.primitive_type || 'unknown';
        pathSegments.push({ displayName, type });

        // Navigate to nested fields
        if (currentField.object_schema?.fields) {
          currentFields = currentField.object_schema.fields;
        } else if (currentField.array && typeof currentField.array === 'object' && 'fields' in currentField.array) {
          currentFields = currentField.array.fields;
        }
      }
    }

    const isRequired = field.required;

    return { pathSegments, isRequired };
  }, [destinationFieldPath, destinationFields]);

  return (
    <Dialog
      usePortal={true}
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className={styles.dialogTitleContainer}>
          <div className={styles.dialogTitle}>Select Source Field</div>
          {destinationFieldInfo && (
            <div className={styles.dialogSubtitle}>
              Mapping to:{' '}
              {destinationFieldInfo.pathSegments.map((segment, index) => (
                <span key={index}>
                  {index > 0 && <span className={styles.pathSeparator}> / </span>}
                  <strong>{segment.displayName}</strong>
                  {/* Only show type badge for the last segment (leaf node) */}
                  {index === destinationFieldInfo.pathSegments.length - 1 && (
                    <Tag minimal className={styles.destinationTypeBadge}>
                      {segment.type}
                    </Tag>
                  )}
                </span>
              ))}
              {destinationFieldInfo.isRequired && (
                <span className={styles.requiredIndicator}>*</span>
              )}
            </div>
          )}
        </div>
      }
      className={styles.dialog}
    >
      <DialogBody>
        <div className={styles.searchContainer}>
          <div className={styles.searchInputWrapper}>
            <InputGroup
              leftIcon="search"
              placeholder="Search source fields..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              rightElement={
                searchQuery ? (
                  <button
                    className={styles.clearButton}
                    onClick={() => setSearchQuery('')}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                ) : undefined
              }
            />
          </div>
          <ButtonGroup>
            <Button
              onClick={() => setActiveFilter('all')}
              active={activeFilter === 'all'}
              className={`${styles.filterButton} ${activeFilter === 'all' ? styles.filterButtonActive : ''}`}
            >
              All
            </Button>
            <Button
              onClick={() => setActiveFilter('compatible')}
              active={activeFilter === 'compatible'}
              className={`${styles.filterButton} ${activeFilter === 'compatible' ? styles.filterButtonActive : ''}`}
            >
              Compatible
            </Button>
            <Button
              onClick={() => setActiveFilter('suggested')}
              active={activeFilter === 'suggested'}
              disabled={compatibleSuggestedCount === 0}
              className={`${styles.filterButton} ${styles.filterButtonSuggested} ${
                activeFilter === 'suggested' ? styles.filterButtonActive : ''
              } ${compatibleSuggestedCount === 0 ? styles.filterButtonDisabled : ''}`}
            >
              Suggested
            </Button>
          </ButtonGroup>
        </div>

        <div className={styles.treeContainer}>
          {filteredTreeData.length > 0 ? (
            filteredTreeData.map((node) => (
              <TreeNode
                key={node.path}
                node={node}
                onSelect={handleSelect}
                expandedNodes={expandedNodes}
                onToggleExpand={handleToggleExpand}
                selectedField={selectedField}
                selectedNodeRef={selectedNodeRef}
                isDisabled={disabledFieldsMap.has(node.path)}
                disabledFieldsMap={disabledFieldsMap}
              />
            ))
          ) : (
            <div className={styles.emptyState}>
              No fields found matching "{searchQuery}"
            </div>
          )}
        </div>
      </DialogBody>
    </Dialog>
  );
};

export default FieldSelectorDialog;
